import { makeAutoObservable } from "mobx";
import {
  MAX_SESSION_TIMEOUT,
  MODAL_TIMEOUT,
  REFRESH_SESSION_INTERVAL,
  USER_INACTIVITY_TIMEOUT,
} from "../utils/enums";

class SessionStore {
  sessionRefreshInterval = null;
  sessionTimeout = null;
  userInactiveTimeout = null;
  sessionModalTimeout = null;

  showContinueSessionModal = false;
  showDataDeletedModal = false;
  dataDeletedWithAcknowledgement = false;

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Keep session alive by refreshing it in interval
   */
  startRefreshSessionInterval = (callback) => {
    this.sessionRefreshInterval = setInterval(
      () => callback(),
      REFRESH_SESSION_INTERVAL
    );
  };

  /**
   * Start session tracking
   * Session tracking is started only if user is unidentified (not logged in to op.fi)
   */
  startSessionTracking = () => {
    this.startMaxSessionTimeout();
    this.trackUserInactivity();
    window.addEventListener("scroll", this.trackUserInactivity, false);
    window.addEventListener("keydown", this.trackUserInactivity, false);
    window.addEventListener("mousedown", this.trackUserInactivity, false);
  };

  /**
   * Stop session tracking
   * Clear inactivity and session timeouts and remove event listeners
   */
  stopSessionTracking = () => {
    clearTimeout(this.userInactiveTimeout);
    clearTimeout(this.sessionTimeout);
    window.removeEventListener("scroll", this.trackUserInactivity, false);
    window.removeEventListener("keydown", this.trackUserInactivity, false);
    window.removeEventListener("mousedown", this.trackUserInactivity, false);
  };

  /**
   * Maximum session timeout
   * Show "continue session" modal after maximum session duration has passed
   * Can be cleared and started over if user chooses to continue form filling
   */
  startMaxSessionTimeout = () => {
    clearTimeout(this.sessionTimeout);

    this.sessionTimeout = setTimeout(() => {
      this.displayContinueSessionModal();
    }, MAX_SESSION_TIMEOUT);
  };

  /**
   * Track user inactivity
   * Start timer, which will reset when user performs scroll or key down event
   * If user is not active within certain time, show "continue session" modal
   */
  trackUserInactivity = () => {
    clearTimeout(this.userInactiveTimeout);
    this.userInactiveTimeout = setTimeout(() => {
      this.displayContinueSessionModal();
    }, USER_INACTIVITY_TIMEOUT);
  };

  /**
   * Display "continue session" modal
   * Show modal in the UI and start timeout for user reaction
   * If user fails to react within the certain time, reset form
   */
  displayContinueSessionModal = () => {
    this.showContinueSessionModal = true;
    this.stopSessionTracking();

    clearTimeout(this.sessionModalTimeout);
    this.sessionModalTimeout = setTimeout(() => {
      this.resetSession(false);
    }, MODAL_TIMEOUT);
  };

  /**
   * Continue session selected
   * When user decides to continue form filling, clear modal timeout and
   * start over maximum session timeout
   */
  continueSession = () => {
    clearTimeout(this.sessionModalTimeout);
    this.showContinueSessionModal = false;
    this.startSessionTracking();
  };

  /**
   * End session selected
   * When user decides to stop form filling, reset session
   */
  endSession = () => {
    this.resetSession(true);
  };

  /**
   * Reset session
   * When session is ended due to inactivity or maximum session duration,
   * clear session modal timeout and refresh interval, update variable whether
   * ending has been user decision or automatic timeout and display
   * "data deleted" modal in the UI
   */
  resetSession = (isUserDecision) => {
    clearTimeout(this.sessionModalTimeout);
    clearInterval(this.sessionRefreshInterval);

    this.showContinueSessionModal = false;
    this.dataDeletedWithAcknowledgement = isUserDecision;
    setTimeout(() => {
      this.showDataDeletedModal = true;
    }, 500);
  };

  /**
   * Clear all timers and event listeners
   */
  clearAll = () => {
    clearInterval(this.sessionRefreshInterval);
    clearTimeout(this.sessionTimeout);
    clearTimeout(this.userInactiveTimeout);
    clearTimeout(this.sessionModalTimeout);

    window.removeEventListener("scroll", this.trackUserInactivity, false);
    window.removeEventListener("keydown", this.trackUserInactivity, false);
    window.removeEventListener("mousedown", this.trackUserInactivity, false);
  };
}

const sessionStore = new SessionStore();

export default sessionStore;
